import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import brandLogo from './assets/image.png';

// Components
import AuthModal from './components/Auth/AuthModal';
import ProtectedRoute from './components/ProtectedRoute';

// Auth Pages
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';

// Admin Pages
import AdminLayout from './pages/Admin/AdminLayout/AdminLayout';
import Dashboard from './pages/Admin/Dashboard/Dashboard';
import AdminProducts from './pages/Admin/Products/AdminProducts';
import AdminCategories from './pages/Admin/Categories/AdminCategories';
import AdminOrders from './pages/Admin/Orders/AdminOrders';
import AdminCustomers from './pages/Admin/Customers/AdminCustomers';
import AdminInventory from './pages/Admin/Inventory/AdminInventory';
import AdminSettings from './pages/Admin/Settings/AdminSettings';
import AdminSlider from './pages/Admin/Slider/AdminSlider';
import AdminSales from './pages/Admin/Sales/AdminSales';

import './index.css';

function App() {
  const { isAuthModalOpen, authMode, closeAuthModal, user, openAuthModal, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);

  const location = useLocation();

  useEffect(() => {
    // Coordinate splash screen with auth loading
    if (!authLoading) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 800); // Faster, snappier load
      return () => clearTimeout(timer);
    }
  }, [authLoading]);

  // Auto-open login if at root and not authenticated
  useEffect(() => {
    if (!isLoading && !authLoading && !user && location.pathname === '/') {
      openAuthModal('login');
    }
  }, [isLoading, authLoading, user, location.pathname, openAuthModal]);

  // Elite Scroll-to-Top Logic
  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="muno-splash-screen">
        <div className="splash-content">
          <div className="splash-logo-container">
            <div className="splash-ring-outer"></div>
            <div className="splash-ring-inner"></div>
            <div className="splash-icon-box">
              <img src={brandLogo} alt="MUNO Logo" className="splash-brand-logo" />
            </div>
          </div>

          <div className="splash-text-wrap">
            <h1 className="splash-brand">MUNO</h1>
            <p className="splash-tagline">SEE IT. GET IT.</p>
          </div>

          <div className="splash-loader-wrap">
            <div className="splash-progress-track">
              <div className="splash-progress-fill"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={closeAuthModal}
        initialMode={authMode}
      />

      <main className="content-wrapper">
        <Routes>
          {/* Root Route: Redirect to Admin if logged in, else show Login Trigger */}
          <Route path="/" element={
            user ? <Navigate to="/admin/dashboard" replace /> : (
              <div className="admin-portal-bg">
                {/* Clean background while modal handles login */}
              </div>
            )
          } />

          {/* Auth Routes */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Admin Routes - Secured */}
          <Route
            path="admin"
            element={
              <ProtectedRoute roleRequired="admin">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="inventory" element={<AdminInventory />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="sliders" element={<AdminSlider />} />
            <Route path="sales" element={<AdminSales />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default App;



